import React, { useEffect, useState } from 'react';

import styled from 'styled-components';

import { CommunityContract } from 'types/contracts';
import { FeeDataType } from 'types/fees';

import { useCareTypesContext } from './CareTypesContext';
import { CommunityFeesType, LivingInfoDataType, LivingInfoGroupedDataType } from './types';
import { sortCommunityContracts } from './utils';
import { COMMUNITY_CONTRACT_TYPE } from '../../../constants';
import { isCommunityCCRC } from '../../Community/utils';
import ButtonPill from '../../lib/ButtonPill';
import GradientDivider from '../../lib/GradientDivider';
import { useCommunityInfoContext } from '../CommunityInfoContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const CareTypeContractSelector = () => {
  const { careType, contract, setContract } = useCareTypesContext();
  const { communityInfo } = useCommunityInfoContext();
  const [careTypeContracts, setCareTypeContracts] = useState<CommunityContract[]>([]);

  useEffect(() => {
    const accommodationContracts = careType?.livingInfo.flatMap((group: LivingInfoGroupedDataType) =>
      group.apartments.flatMap((item: LivingInfoDataType) => item?.contracts || [])
    );
    const feesContracts = careType?.communityFees.flatMap((item: CommunityFeesType) => item?.contracts || []);
    const careFeeContracts = careType?.fees.flatMap((item: FeeDataType) => item?.contracts || []);

    const allContracts = [...(accommodationContracts ?? []), ...(feesContracts ?? []), ...(careFeeContracts ?? [])];

    const seen = new Set();
    const uniqueContracts = allContracts.filter((item: CommunityContract) => {
      if (seen.has(item.slug)) return false;
      seen.add(item.slug);
      return true;
    });

    setCareTypeContracts(uniqueContracts);
    if (uniqueContracts.length) {
      const sortedContracts = uniqueContracts.sort(sortCommunityContracts);
      setContract(sortedContracts[0]);
    }
  }, [careType]);

  if (!isCommunityCCRC(communityInfo?.rental_types || [])) {
    return null;
  }

  return (
    <Container>
      <ButtonContainer>
        {careTypeContracts.sort(sortCommunityContracts).map((item: CommunityContract) => {
          const isSelected = item.id === contract?.id;
          const buttonType = isSelected ? 'primary' : 'default';
          return (
            <ButtonPill
              data-test-id={`cd-contract-switch-type-${item.type}-refund-${item.refund_percentage}`}
              type={buttonType}
              style={{ height: 26, fontSize: 12, border: 'none' }}
              key={item.id}
              onClick={() => setContract(item)}
            >
              {item.name}
              {item.type !== COMMUNITY_CONTRACT_TYPE.RENTAL && ` ${item.refund_percentage} %`}
            </ButtonPill>
          );
        })}
      </ButtonContainer>
      <GradientDivider className="flat" />
    </Container>
  );
};

export default CareTypeContractSelector;
